import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

const AdvertiserAddMoney = () => {
  const navigate = useNavigate();
  const [showAddForm, setShowAddForm] = useState(false);
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  const quickAmounts = [2000, 5000, 10000];

  useEffect(() => {
    fetchWalletBalance();
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const fetchWalletBalance = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/get/wallet/detail`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setWalletBalance(response.data.balance || 0);
    } catch (error) {
      console.error("Error fetching wallet balance:", error);
    }
  };

  const handleAmountSelect = (value) => {
    setAmount(value.toString());
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (value === "" || (/^\d+$/.test(value) && parseInt(value) >= 0)) {
      setAmount(value);
    }
  };

  const initiatePayment = async () => {
    try {
      setLoading(true);
      const paymentAmount = parseInt(amount);
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/wallet/topup`,
        { amount: paymentAmount, currency: "INR" },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error initiating payment:", error);
      toast.error("Failed to initiate payment. Please try again.");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const openRazorpayCheckout = (paymentData) => {
    const { order_id, amount, currency } = paymentData;
    const options = {
      key: paymentData.key || process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount,
      currency,
      order_id,
      name: "Ad Submission Payment",
      description: "Payment to post your ad",
      handler: function (response) {
        handlePaymentSuccess(response.razorpay_payment_id);
      },
      prefill: {
        name: localStorage.getItem("userName") || "",
        email: localStorage.getItem("userEmail") || "",
        contact: localStorage.getItem("userPhone") || "",
      },
      theme: { color: "#3B82F6" },
      modal: {
        ondismiss: function () {
          setIsProcessing(false);
          toast.warning("Payment cancelled");
        },
      },
    };
    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  const handlePaymentSuccess = async (paymentId) => {
    try {
      setIsProcessing(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/wallet/success/${paymentId}`,
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      const status = response.data.status;
      if (status === "captured" || status === "success") {
        await fetchWalletBalance();
        toast.success(`₹${amount} successfully added to your wallet!`);
        setAmount("");
        setShowAddForm(false);
      } else {
        toast.error("Payment verification failed. Please contact support.");
      }
    } catch (error) {
      console.error("Error verifying payment:", error);
      toast.error("Payment verification failed. Please contact support.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePayNow = async () => {
    if (!amount || parseInt(amount) < 100) {
      toast.error("Minimum amount is ₹100");
      return;
    }
    if (parseInt(amount) > 100000) {
      toast.error("Maximum amount is ₹1,00,000");
      return;
    }
    setIsProcessing(true);
    const paymentData = await initiatePayment();
    if (paymentData && paymentData.order_id) {
      openRazorpayCheckout(paymentData);
    } else {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl mx-auto pb-20 sm:pb-8">
      {/* Balance Card - Premium Design */}
      <div className="bg-gradient-to-br from-indigo-600 to-blue-700 rounded-[2.5rem] p-8 text-white shadow-2xl shadow-indigo-200 relative overflow-hidden group">
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-4 opacity-80">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-xs font-black uppercase tracking-widest">Active Balance</p>
          </div>
          <p className="text-4xl sm:text-5xl font-black tracking-tight">₹{walletBalance.toLocaleString()}</p>
          <div className="mt-6 flex gap-2">
            <span className="px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-[10px] font-bold">Verified Account</span>
            <span className="px-3 py-1 bg-green-400/20 backdrop-blur-md rounded-full text-[10px] font-bold text-green-200">Refined for Business</span>
          </div>
        </div>
        {/* Abstract background decorator */}
        <div className="absolute -bottom-10 -right-10 w-48 h-48 bg-white/10 rounded-full blur-3xl group-hover:scale-125 transition-transform duration-700"></div>
      </div>

      {!showAddForm ? (
        <div className="bg-white rounded-[2rem] border border-gray-100 p-8 text-center space-y-6 shadow-sm">
          <div className="w-20 h-20 bg-blue-50 rounded-3xl flex items-center justify-center mx-auto text-blue-600 animate-bounce-slow">
            <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-black text-gray-900">Add Advertising Funds</h2>
            <p className="text-sm text-gray-500 font-medium px-4">Top up your wallet to launch new campaigns and increase your brand visibility.</p>
          </div>
          <button
            onClick={() => setShowAddForm(true)}
            className="w-full py-4 bg-gray-900 text-white rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-gray-800 active:scale-95 transition-all shadow-lg shadow-gray-200"
          >
            Initiate Top-up
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Amount Input */}
          <div className="bg-white rounded-[2rem] border border-gray-100 p-6 sm:p-8 shadow-sm space-y-6">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Enter Deposit Amount</label>
              <div className="relative">
                <span className="absolute left-6 top-1/2 -translate-y-1/2 text-2xl font-black text-gray-300">₹</span>
                <input
                  type="text"
                  value={amount}
                  onChange={handleAmountChange}
                  className="w-full pl-12 pr-6 py-6 bg-gray-50 border-2 border-transparent focus:border-blue-500 focus:bg-white rounded-3xl text-2xl font-black text-gray-900 outline-none transition-all placeholder:text-gray-200"
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* Quick Presets */}
            <div className="grid grid-cols-3 gap-3">
              {quickAmounts.map((q) => (
                <button
                  key={q}
                  onClick={() => handleAmountSelect(q)}
                  className={`py-4 rounded-2xl text-xs font-black transition-all active:scale-90 ${amount === q.toString()
                    ? "bg-blue-600 text-white shadow-lg shadow-blue-100"
                    : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                    }`}
                >
                  ₹{q.toLocaleString()}
                </button>
              ))}
            </div>
          </div>

          {/* Notes Card */}
          <div className="bg-amber-50 rounded-[2rem] p-6 space-y-4 border border-amber-100">
            <h3 className="text-xs font-black text-amber-800 uppercase tracking-widest flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Payment Terms
            </h3>
            <ul className="space-y-3">
              {[
                "Funds are valid for a duration of 4 years.",
                "Non-refundable and non-transferable to bank accounts.",
                "Exclusively for advertisement campaign payments."
              ].map((note, i) => (
                <li key={i} className="flex gap-3 text-xs text-amber-700/80 font-bold leading-relaxed">
                  <span className="mt-1.5 w-1.5 h-1.5 bg-amber-400 rounded-full shrink-0" />
                  {note}
                </li>
              ))}
            </ul>
          </div>

          {/* Action Footer */}
          <div className="flex gap-3">
            <button
              onClick={() => setShowAddForm(false)}
              className="px-6 py-4 bg-white border border-gray-100 text-gray-500 font-black text-xs uppercase tracking-widest rounded-2xl active:scale-95 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handlePayNow}
              disabled={loading || isProcessing || !amount}
              className="flex-1 py-4 bg-blue-600 text-white font-black text-xs uppercase tracking-widest rounded-2xl shadow-xl shadow-blue-100 active:scale-95 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
            >
              {(loading || isProcessing) ? (
                <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
              ) : "Authorize & Pay"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvertiserAddMoney;
